/**
 * Generic React Query hook set for a support handler queue (org-admin
 * account-lane or super-admin platform-lane). Both queues share identical
 * list/detail/reply/update/relane shapes (see supportAdminService.ts /
 * supportPlatformService.ts) — this factory is instantiated once per queue
 * (useSupportAdminQueue / usePlatformSupportQueue below) instead of
 * duplicating the query/mutation wiring for each.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { markRequestRead } from '../services/api/supportService';
import supportAdminService, {
  type ListSupportQueueParams,
  type PaginatedSupportRequests,
} from '../services/api/supportAdminService';
import supportPlatformService, {
  type ListPlatformSupportParams,
} from '../services/api/supportPlatformService';
import type {
  SupportAssignableHandler,
  SupportRequesterOption,
  SupportReplyInput,
  SupportRequestDetail,
  UpdateSupportRequestInput,
} from '@upskill/shared';

interface QueueService<TListParams> {
  listRequests: (params?: TListParams) => Promise<PaginatedSupportRequests>;
  getUnreadCount: () => Promise<number>;
  getRequestDetail: (id: string) => Promise<SupportRequestDetail>;
  replyToRequest: (id: string, input: SupportReplyInput) => Promise<SupportRequestDetail>;
  updateRequest: (id: string, input: UpdateSupportRequestInput) => Promise<SupportRequestDetail>;
  /** Escalate (org-admin) or bounce (platform) — one re-lane action per queue. */
  relane: (id: string) => Promise<SupportRequestDetail>;
  listAssignableHandlers: () => Promise<SupportAssignableHandler[]>;
  listRequesters: () => Promise<SupportRequesterOption[]>;
}

function createSupportQueueHooks<TListParams extends { page?: number; limit?: number }>(
  keyPrefix: string,
  service: QueueService<TListParams>,
) {
  const LIST_KEY = [keyPrefix, 'requests'] as const;
  const UNREAD_KEY = [keyPrefix, 'unread-count'] as const;
  const LOOKUPS_KEY = [keyPrefix, 'lookups'] as const;
  const detailKey = (id: string) => [keyPrefix, 'request', id] as const;

  function useQueueRequests(params: TListParams) {
    const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);
    return useQuery({
      queryKey: [...LIST_KEY, params],
      queryFn: () => service.listRequests(params),
      enabled: isReady,
      retry: false,
      staleTime: 15_000,
    });
  }

  // `enabled` lets callers (e.g. the sidebar badge) gate this on a permission
  // check too, not just auth — the endpoint 403s for non-handlers, so there's
  // no reason to fire it before the caller knows the viewer can see the queue.
  function useQueueUnreadCount(options?: { enabled?: boolean }) {
    const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);
    const enabled = isReady && (options?.enabled ?? true);
    return useQuery({
      queryKey: UNREAD_KEY,
      queryFn: () => service.getUnreadCount(),
      refetchInterval: enabled ? 15_000 : false,
      staleTime: 10_000,
      enabled,
      retry: false,
    });
  }

  function useQueueDetail(id: string | null | undefined) {
    const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);
    return useQuery({
      queryKey: id ? detailKey(id) : [keyPrefix, 'request', 'none'],
      queryFn: () => service.getRequestDetail(id as string),
      enabled: isReady && !!id,
      retry: false,
    });
  }

  function invalidateQueueLists(queryClient: ReturnType<typeof useQueryClient>) {
    queryClient.invalidateQueries({ queryKey: LIST_KEY });
    queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
  }

  function useQueueReply(id: string | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (input: SupportReplyInput) => {
        if (!id) throw new Error('useQueueReply: id is required');
        return service.replyToRequest(id, input);
      },
      onSuccess: (updated) => {
        queryClient.setQueryData(detailKey(updated.id), updated);
        invalidateQueueLists(queryClient);
      },
    });
  }

  function useQueueUpdate(id: string | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (input: UpdateSupportRequestInput) => {
        if (!id) throw new Error('useQueueUpdate: id is required');
        return service.updateRequest(id, input);
      },
      onSuccess: (updated) => {
        queryClient.setQueryData(detailKey(updated.id), updated);
        invalidateQueueLists(queryClient);
      },
    });
  }

  function useQueueRelane(id: string | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () => {
        if (!id) throw new Error('useQueueRelane: id is required');
        return service.relane(id);
      },
      onSuccess: (updated) => {
        queryClient.setQueryData(detailKey(updated.id), updated);
        invalidateQueueLists(queryClient);
      },
    });
  }

  // Marking a thread read is the SAME endpoint the widget uses
  // (`POST /support/requests/:id/read`) — it authorizes via `assertReach`,
  // which admits handlers as well as requesters, so there's no separate
  // admin/platform read-state route. This just points that shared call at
  // this queue's own list/unread-count cache keys so the badge/list dot
  // clear immediately instead of waiting for the next 15s poll.
  function useQueueMarkRead() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => markRequestRead(id),
      onSuccess: () => invalidateQueueLists(queryClient),
    });
  }

  // Handler roster + requester roster: small, slow-changing lookup lists (not
  // paginated) that back the reassign picker and the requester/org filters.
  // Long staleTime — no reason to refetch a roster on every filter tweak.
  function useQueueAssignableHandlers() {
    const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);
    return useQuery({
      queryKey: [...LOOKUPS_KEY, 'assignable-handlers'],
      queryFn: () => service.listAssignableHandlers(),
      enabled: isReady,
      staleTime: 60_000,
      retry: false,
    });
  }

  function useQueueRequesters() {
    const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);
    return useQuery({
      queryKey: [...LOOKUPS_KEY, 'requesters'],
      queryFn: () => service.listRequesters(),
      enabled: isReady,
      staleTime: 60_000,
      retry: false,
    });
  }

  return {
    useQueueRequests,
    useQueueUnreadCount,
    useQueueDetail,
    useQueueReply,
    useQueueUpdate,
    useQueueRelane,
    useQueueMarkRead,
    useQueueAssignableHandlers,
    useQueueRequesters,
  };
}

export const {
  useQueueRequests: useSupportAdminRequests,
  useQueueUnreadCount: useSupportAdminUnreadCount,
  useQueueDetail: useSupportAdminDetail,
  useQueueReply: useSupportAdminReply,
  useQueueUpdate: useSupportAdminUpdate,
  useQueueRelane: useSupportAdminEscalate,
  useQueueMarkRead: useSupportAdminMarkRead,
  useQueueAssignableHandlers: useSupportAdminAssignableHandlers,
  useQueueRequesters: useSupportAdminRequesters,
} = createSupportQueueHooks<ListSupportQueueParams>('support-admin', {
  ...supportAdminService,
  relane: supportAdminService.escalateRequest,
});

export const {
  useQueueRequests: usePlatformSupportRequests,
  useQueueUnreadCount: usePlatformSupportUnreadCount,
  useQueueDetail: usePlatformSupportDetail,
  useQueueReply: usePlatformSupportReply,
  useQueueUpdate: usePlatformSupportUpdate,
  useQueueRelane: usePlatformSupportBounce,
  useQueueMarkRead: usePlatformSupportMarkRead,
  useQueueAssignableHandlers: usePlatformSupportAssignableHandlers,
  useQueueRequesters: usePlatformSupportRequesters,
} = createSupportQueueHooks<ListPlatformSupportParams>('support-platform', {
  ...supportPlatformService,
  relane: supportPlatformService.bounceRequest,
});
